import { createAsyncThunk } from '@reduxjs/toolkit';
import HttpError from '@common/interfaces/api/http-error';
import ValidationResult from '@common/interfaces/api/validation-result';
import generalDataService from '../../services/data/general-data-service';
import {
  returnAxiosOrGeneralFetchError,
  returnAxiosValidationOrGeneralFetchError,
} from '../../services/data/axios-base';
import LoginDetails, { LoginResponse } from '../../models/general/login-details';
import CurrentUser, {
  RobotSubscriptions,
  OrganizationSubscriptions,
} from '../../models/general/current-user';
import {
  TwoFactorSetupConfirmResponse,
  TwoFactorSetupInitResponse,
  VerifyTwoFactorRequest,
} from '../../models/general/two-factor';
import Filters from '@common/enums/query-params/filters';
import ListAgreement from '../../models/general/agreements/list-agreement';
import AgreementDetails from '../../models/general/agreements/agreement-details';
import AcceptanceForm from '../../models/general/agreements/acceptance-form';
import Acceptance from '../../models/general/agreements/acceptance';
import CustomLocalization from '../../models/general/custom-localizations/custom-localization';
import NotificationSubscription from '../../models/general/notifications/notification-trigger';
import UpdateVersion from '../../models/general/versions/update-version';
import FormUser from '../../models/general/form-user';
import FrontendParams from '../../models/general/frontend-params';
import ReportDataSet from '../../models/general/notifications/report-data-set';
import ReportParameters from '../../models/general/notifications/report-parameters';
import SetPasswordDetails from '../../models/general/set-password-details';
import VersionSet from '../../models/general/versions/version-set';
import TorizonVersionSet from '../../models/general/versions/torizon-version-set';
import AssignableRobot from '../../models/general/versions/assignable-robot';
import TorizonPackage from '../../models/general/versions/torizon-package';

export const login = createAsyncThunk<
  LoginResponse,
  Partial<LoginDetails>,
  { rejectValue: HttpError | ValidationResult[] }
>('login', async (loginDetails: Partial<LoginDetails>, { rejectWithValue }) => {
  try {
    return await generalDataService.login(loginDetails);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const verifyTwoFactor = createAsyncThunk<
  { token: string; user: CurrentUser },
  VerifyTwoFactorRequest,
  { rejectValue: HttpError | ValidationResult[] }
>('verify-two-factor', async (data, { rejectWithValue }) => {
  try {
    return await generalDataService.verifyTwoFactor(data);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const initTwoFactorSetup = createAsyncThunk<
  TwoFactorSetupInitResponse,
  string | undefined,
  { rejectValue: HttpError | ValidationResult[] }
>('init-two-factor-setup', async (pendingToken, { rejectWithValue }) => {
  try {
    return await generalDataService.initTwoFactorSetup(pendingToken);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const confirmTwoFactorSetup = createAsyncThunk<
  TwoFactorSetupConfirmResponse,
  { otp: string; pendingToken?: string },
  { rejectValue: HttpError | ValidationResult[] }
>('confirm-two-factor-setup', async ({ otp, pendingToken }, { rejectWithValue }) => {
  try {
    return await generalDataService.confirmTwoFactorSetup(otp, pendingToken);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const cancelTwoFactorSetup = createAsyncThunk<
  void,
  void,
  { rejectValue: HttpError | ValidationResult[] }
>('cancel-two-factor-setup', async (_, { rejectWithValue }) => {
  try {
    return await generalDataService.cancelTwoFactorSetup();
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const disableTwoFactor = createAsyncThunk<
  void,
  { currentPassword?: string; googleToken?: string; otp?: string; backupCode?: string },
  { rejectValue: HttpError | ValidationResult[] }
>(
  'disable-two-factor',
  async ({ currentPassword, googleToken, otp, backupCode }, { rejectWithValue }) => {
    try {
      return await generalDataService.disableTwoFactor(
        currentPassword,
        googleToken,
        otp,
        backupCode,
      );
    } catch (error) {
      return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
    }
  },
);

export const changePassword = createAsyncThunk<
  void,
  { currentPassword?: string; googleToken?: string; newPassword: string; otp?: string },
  { rejectValue: HttpError | ValidationResult[] }
>(
  'change-password',
  async ({ currentPassword, googleToken, newPassword, otp }, { rejectWithValue }) => {
    try {
      return await generalDataService.changePassword(
        currentPassword,
        googleToken,
        newPassword,
        otp,
      );
    } catch (error) {
      return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
    }
  },
);

export const resetUserTwoFactor = createAsyncThunk<
  void,
  string,
  { rejectValue: HttpError | ValidationResult[] }
>('reset-user-two-factor', async (userId, { rejectWithValue }) => {
  try {
    return await generalDataService.resetUserTwoFactor(userId);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const postResetPasswordEmail = createAsyncThunk<
  void,
  string,
  { rejectValue: HttpError | ValidationResult[] }
>('reset-password-request', async (email, { rejectWithValue }) => {
  try {
    return await generalDataService.postResetPasswordEmail(email);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const setPassword = createAsyncThunk<
  LoginResponse,
  SetPasswordDetails,
  { rejectValue: HttpError | ValidationResult[] }
>('set-password', async (data, { rejectWithValue }) => {
  try {
    return await generalDataService.setPassword(data);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const getUserByToken = createAsyncThunk<CurrentUser, void, { rejectValue: HttpError }>(
  'users/get-by-token',
  async (_, { rejectWithValue }) => {
    try {
      return await generalDataService.getUserByToken();
    } catch (error) {
      return rejectWithValue(returnAxiosOrGeneralFetchError(error));
    }
  },
);

export const getAgreements = createAsyncThunk<ListAgreement[], void, { rejectValue: HttpError }>(
  'agreements/get',
  async (_, { rejectWithValue }) => {
    try {
      return await generalDataService.getAgreements();
    } catch (error) {
      return rejectWithValue(returnAxiosOrGeneralFetchError(error));
    }
  },
);

export const getAgreementById = createAsyncThunk<
  AgreementDetails,
  string,
  { rejectValue: HttpError }
>('agreements/retrieve', async (id: string, { rejectWithValue }) => {
  try {
    return await generalDataService.getAgreementById(id);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const createAgreement = createAsyncThunk<
  ListAgreement,
  Partial<ListAgreement>,
  { rejectValue: HttpError | ValidationResult[] }
>('agreements/post', async (data: Partial<ListAgreement>, { rejectWithValue }) => {
  try {
    return await generalDataService.createAgreement(data);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const patchAgreementById = createAsyncThunk<
  ListAgreement,
  { id: string; data: Partial<ListAgreement> },
  { rejectValue: HttpError | ValidationResult[] }
>(
  'agreements/patch',
  async (data: { id: string; data: Partial<ListAgreement> }, { rejectWithValue }) => {
    try {
      return await generalDataService.patchAgreementById(data.id, data.data);
    } catch (error) {
      return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
    }
  },
);

export const deleteAgreementById = createAsyncThunk<string, string, { rejectValue: HttpError }>(
  'agreements/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      return await generalDataService.deleteAgreementById(id);
    } catch (error) {
      return rejectWithValue(returnAxiosOrGeneralFetchError(error));
    }
  },
);

export const uploadAgreementFile = createAsyncThunk<
  AgreementDetails,
  FormData,
  { rejectValue: HttpError | ValidationResult[] }
>('agreements/files/upload', async (formData: FormData, { rejectWithValue, dispatch }) => {
  try {
    const updatePercentageCompleteHandler = (percentageComplete: number) => {
      dispatch({ type: 'general/setAgreementFileUploadProgress', payload: percentageComplete });
    };

    return await generalDataService.postUploadAgreementFile(
      updatePercentageCompleteHandler,
      formData,
    );
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const deleteAgreementFileById = createAsyncThunk<
  string,
  { agreementId: string; fileId: string },
  { rejectValue: HttpError }
>(
  'agreements/files/delete',
  async (data: { agreementId: string; fileId: string }, { rejectWithValue }) => {
    try {
      return await generalDataService.deleteAgreementFileById(data.agreementId, data.fileId);
    } catch (error) {
      return rejectWithValue(returnAxiosOrGeneralFetchError(error));
    }
  },
);

export const createAgreementAcceptance = createAsyncThunk<
  Acceptance,
  Partial<AcceptanceForm>,
  { rejectValue: HttpError | ValidationResult[] }
>('agreements/accept', async (data: Partial<AcceptanceForm>, { rejectWithValue }) => {
  try {
    return await generalDataService.createAgreementAcceptance(data);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const getCustomLocalizations = createAsyncThunk<
  CustomLocalization[],
  void,
  { rejectValue: HttpError }
>('localizations/get', async (_, { rejectWithValue }) => {
  try {
    return await generalDataService.getCustomLocalizations();
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const getCustomLocalizationById = createAsyncThunk<
  CustomLocalization,
  string,
  { rejectValue: HttpError }
>('localizations/retrieve', async (id: string, { rejectWithValue }) => {
  try {
    return await generalDataService.getCustomLocalizationById(id);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const createCustomLocalization = createAsyncThunk<
  CustomLocalization,
  Partial<CustomLocalization>,
  { rejectValue: HttpError | ValidationResult[] }
>('localizations/post', async (data: Partial<CustomLocalization>, { rejectWithValue }) => {
  try {
    return await generalDataService.createCustomLocalization(data);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const patchCustomLocalizationById = createAsyncThunk<
  CustomLocalization,
  { id: string; data: Partial<CustomLocalization> },
  { rejectValue: HttpError | ValidationResult[] }
>(
  'localizations/patch',
  async (data: { id: string; data: Partial<CustomLocalization> }, { rejectWithValue }) => {
    try {
      return await generalDataService.patchCustomLocalizationById(data.id, data.data);
    } catch (error) {
      return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
    }
  },
);

export const deleteCustomLocalizationById = createAsyncThunk<
  string,
  string,
  { rejectValue: HttpError }
>('localizations/delete', async (id: string, { rejectWithValue }) => {
  try {
    return await generalDataService.deleteCustomLocalizationById(id);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const getNotificationSubscriptions = createAsyncThunk<
  NotificationSubscription[],
  void,
  { rejectValue: HttpError }
>('notifications/get', async (_, { rejectWithValue }) => {
  try {
    return await generalDataService.getNotificationSubscriptions();
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const createNotificationSubscription = createAsyncThunk<
  NotificationSubscription,
  Partial<NotificationSubscription>,
  { rejectValue: HttpError | ValidationResult[] }
>('notifications/post', async (data: Partial<NotificationSubscription>, { rejectWithValue }) => {
  try {
    return await generalDataService.createNotificationSubscription(data);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const patchNotificationSubscriptionById = createAsyncThunk<
  NotificationSubscription,
  { id: string; data: Partial<NotificationSubscription> },
  { rejectValue: HttpError | ValidationResult[] }
>(
  'notifications/patch',
  async (data: { id: string; data: Partial<NotificationSubscription> }, { rejectWithValue }) => {
    try {
      return await generalDataService.patchNotificationSubscriptionById(data.id, data.data);
    } catch (error) {
      return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
    }
  },
);

export const deleteNotificationSubscriptionById = createAsyncThunk<
  string,
  string,
  { rejectValue: HttpError }
>('notifications/delete', async (id: string, { rejectWithValue }) => {
  try {
    return await generalDataService.deleteNotificationSubscriptionById(id);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const registerNotificationDevice = createAsyncThunk<
  string,
  string,
  { rejectValue: HttpError }
>('register-device/post', async (token: string, { rejectWithValue }) => {
  try {
    return await generalDataService.registerNotificationDevice(token);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const getUpdateVersions = createAsyncThunk<
  UpdateVersion[],
  void,
  { rejectValue: HttpError }
>('versions/get', async (_, { rejectWithValue }) => {
  try {
    return await generalDataService.getUpdateVersions();
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const getTorizonPackages = createAsyncThunk<
  TorizonPackage[],
  void,
  { rejectValue: HttpError }
>('torizon-packages/get', async (_, { rejectWithValue }) => {
  try {
    return await generalDataService.getTorizonPackages();
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const createUpdateVersion = createAsyncThunk<
  UpdateVersion,
  Partial<UpdateVersion>,
  { rejectValue: HttpError | ValidationResult[] }
>('versions/post', async (data: Partial<UpdateVersion>, { rejectWithValue }) => {
  try {
    return await generalDataService.createUpdateVersion(data);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const patchUpdateVersionById = createAsyncThunk<
  UpdateVersion,
  { id: string; data: Partial<UpdateVersion> },
  { rejectValue: HttpError | ValidationResult[] }
>(
  'versions/patch',
  async (data: { id: string; data: Partial<UpdateVersion> }, { rejectWithValue }) => {
    try {
      return await generalDataService.patchUpdateVersionById(data.id, data.data);
    } catch (error) {
      return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
    }
  },
);

export const deleteUpdateVersionById = createAsyncThunk<string, string, { rejectValue: HttpError }>(
  'versions/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      return await generalDataService.deleteUpdateVersionById(id);
    } catch (error) {
      return rejectWithValue(returnAxiosOrGeneralFetchError(error));
    }
  },
);

export const getVersionSets = createAsyncThunk<VersionSet[], void, { rejectValue: HttpError }>(
  'version-sets/get',
  async (_, { rejectWithValue }) => {
    try {
      return await generalDataService.getVersionSets();
    } catch (error) {
      return rejectWithValue(returnAxiosOrGeneralFetchError(error));
    }
  },
);

export const createVersionSet = createAsyncThunk<
  VersionSet,
  Partial<VersionSet>,
  { rejectValue: HttpError | ValidationResult[] }
>('version-sets/post', async (data, { rejectWithValue }) => {
  try {
    return await generalDataService.createVersionSet(data);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const patchVersionSetById = createAsyncThunk<
  VersionSet,
  { id: string; data: Partial<VersionSet> },
  { rejectValue: HttpError | ValidationResult[] }
>('version-sets/patch', async (data, { rejectWithValue }) => {
  try {
    return await generalDataService.patchVersionSetById(data.id, data.data);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const deleteVersionSetById = createAsyncThunk<string, string, { rejectValue: HttpError }>(
  'version-sets/delete',
  async (id, { rejectWithValue }) => {
    try {
      return await generalDataService.deleteVersionSetById(id);
    } catch (error) {
      return rejectWithValue(returnAxiosOrGeneralFetchError(error));
    }
  },
);

export const getTorizonVersionSets = createAsyncThunk<
  TorizonVersionSet[],
  void,
  { rejectValue: HttpError }
>('torizon-version-sets/get', async (_, { rejectWithValue }) => {
  try {
    return await generalDataService.getTorizonVersionSets();
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const createTorizonVersionSet = createAsyncThunk<
  TorizonVersionSet,
  Partial<TorizonVersionSet>,
  { rejectValue: HttpError | ValidationResult[] }
>('torizon-version-sets/post', async (data, { rejectWithValue }) => {
  try {
    return await generalDataService.createTorizonVersionSet(data);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const patchTorizonVersionSetById = createAsyncThunk<
  TorizonVersionSet,
  { id: string; data: Partial<TorizonVersionSet> },
  { rejectValue: HttpError | ValidationResult[] }
>('torizon-version-sets/patch', async (data, { rejectWithValue }) => {
  try {
    return await generalDataService.patchTorizonVersionSetById(data.id, data.data);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const deleteTorizonVersionSetById = createAsyncThunk<
  string,
  string,
  { rejectValue: HttpError }
>('torizon-version-sets/delete', async (id, { rejectWithValue }) => {
  try {
    return await generalDataService.deleteTorizonVersionSetById(id);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const getAssignableRobots = createAsyncThunk<
  AssignableRobot[],
  void,
  { rejectValue: HttpError }
>('torizon-version-sets/assignable-robots/get', async (_, { rejectWithValue }) => {
  try {
    return await generalDataService.getAssignableRobots();
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const assignTorizonVersionSet = createAsyncThunk<
  { id: string; robotIds: string[] },
  { id: string; robotIds: string[] },
  { rejectValue: HttpError }
>('torizon-version-sets/assign', async (data, { rejectWithValue }) => {
  try {
    await generalDataService.assignTorizonVersionSet(data.id, data.robotIds);
    return data;
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const validateUserInvite = createAsyncThunk<
  { organizationId: string },
  { robotId: string; token: string },
  { rejectValue: HttpError }
>('register/validate-invite', async (payload, { rejectWithValue }) => {
  try {
    return await generalDataService.validateInvite({ it: payload.token, rid: payload.robotId });
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const registerUser = createAsyncThunk<
  void,
  { data: Partial<FormUser>; filters: Filters },
  { rejectValue: HttpError | ValidationResult[] }
>('register/post', async (payload, { rejectWithValue }) => {
  try {
    return await generalDataService.registerUser(payload.data, payload.filters);
  } catch (error) {
    return rejectWithValue(returnAxiosValidationOrGeneralFetchError(error));
  }
});

export const getFrontendParams = createAsyncThunk<FrontendParams, void, { rejectValue: HttpError }>(
  'frontend-params/get',
  async (_, { rejectWithValue }) => {
    try {
      return await generalDataService.getFrontendParams();
    } catch (error) {
      return rejectWithValue(returnAxiosOrGeneralFetchError(error));
    }
  },
);

export const postSubscribeToOrganizationById = createAsyncThunk<
  OrganizationSubscriptions[],
  string,
  { rejectValue: HttpError }
>('organizations/subscribe', async (id: string, { rejectWithValue }) => {
  try {
    return await generalDataService.postSubscribeToOrganizationById(id);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const postSubscribeToRobotById = createAsyncThunk<
  RobotSubscriptions[],
  string,
  { rejectValue: HttpError }
>('robots/subscribe', async (id: string, { rejectWithValue }) => {
  try {
    return await generalDataService.postSubscribeToRobotById(id);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});

export const getReportDataSets = createAsyncThunk<
  ReportDataSet[],
  ReportParameters,
  { rejectValue: HttpError }
>('notifications/report-data', async (params, { rejectWithValue }) => {
  try {
    return await generalDataService.getReportDataSets(params);
  } catch (error) {
    return rejectWithValue(returnAxiosOrGeneralFetchError(error));
  }
});
